import { useState, useEffect, useMemo, useRef } from 'react';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db, storage } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { slugify, type BlogPost } from '../data/blog';
import { API_BASE_URL } from '@/utils/env';
import AdminLayout from '../components/AdminLayout';
import {
  Save, CheckCircle2, Trash2, Plus, PenLine, ExternalLink, Sparkles,
  ChevronDown, ChevronRight, ImageIcon, ImageDown, Upload, Globe, EyeOff, Search, Copy, Loader2,
} from 'lucide-react';

/* Admin editor for OUR articles — Firestore `blog/current` = { posts: BlogPost[] }.
   One store, two destinations: publishing a post makes it appear on the public
   /blog AND in the signed-in /articles view (both read the same doc via
   fnt/src/data/blog.ts), filtered by the post's `brand`.

   Publish/unpublish writes to Firestore immediately rather than waiting for
   "Save all" — going live is the one action where a forgotten save is a bug. */

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:border-[var(--brand)]';
const areaCls = `${inputCls} leading-relaxed`;
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

const todayISO = () => new Date().toISOString().slice(0, 10);

const blank = (): BlogPost => ({
  id: 'p' + Date.now(),
  slug: '',
  title: '',
  excerpt: '',
  body: '',
  category: 'Insight',
  image: '',
  author: 'Conncct',
  readTime: '5 min read',
  publishedAt: todayISO(),
  published: false,
  brand: 'both',
});

/** Rough read time from the body, so nobody has to guess the number. */
const readTimeFor = (body: string) => {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 220))} min read`;
};

/** Fill and de-duplicate slugs. A blank or repeated slug makes a post
    unreachable or shadows another, since /blog/:slug resolves by first match. */
function withCleanSlugs(list: BlogPost[]): BlogPost[] {
  const seen = new Set<string>();
  return list.map(p => {
    let s = (p.slug || '').trim() || slugify(p.title);
    if (seen.has(s)) { let n = 2; while (seen.has(`${s}-${n}`)) n++; s = `${s}-${n}`; }
    seen.add(s);
    return { ...p, slug: s };
  });
}

type Filter = 'all' | 'live' | 'draft';

export default function AdminBlog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);      // publishing / uploading
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [genView, setGenView] = useState<'consulting' | 'accounting'>('consulting');
  const [generating, setGenerating] = useState(false);
  const [headlines, setHeadlines] = useState<any[]>([]);
  const [writingUrl, setWritingUrl] = useState<string | null>(null);
  const [grabbingId, setGrabbingId] = useState<string | null>(null);
  // Per-post article URL for the "from source" grab. Deliberately NOT stored on
  // the post: pasting a link to borrow a picture shouldn't rewrite the credit
  // line the published article shows.
  const [grabUrl, setGrabUrl] = useState<Record<string, string>>({});
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'blog', 'current'));
        if (snap.exists() && Array.isArray(snap.data().posts)) setPosts(snap.data().posts);
      } catch (e) { console.error('Failed to load blog/current:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const patch = (id: string, fn: (p: BlogPost) => void) => {
    setPosts(prev => prev.map(x => {
      if (x.id !== id) return x;
      const next = { ...x };
      fn(next);
      return next;
    }));
    setDirty(true);
    setSavedAt(null);
  };

  /** Single writer to Firestore. Returns the cleaned list it persisted. */
  const persist = async (list: BlogPost[], note: string) => {
    const cleaned = withCleanSlugs(list);
    await saveAdminConfig('blog/current', { posts: cleaned });
    logAdminAction('config.update', `blog/current: ${note}`);
    setPosts(cleaned);
    setDirty(false);
    setSavedAt(new Date().toLocaleTimeString());
    return cleaned;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persist(posts, 'save all');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied'
        ? 'Save failed. Permission denied. Sign in as a Firebase-authenticated admin.'
        : 'Save failed. Check the console.');
    } finally { setSaving(false); }
  };

  /**
   * Publish or unpublish one post and write it straight away, so the change is
   * live on /blog and /articles without a separate save step.
   */
  const togglePublish = async (p: BlogPost) => {
    const goingLive = !p.published;
    if (goingLive) {
      if (!p.title.trim() || !p.body.trim()) {
        showAlert('Give the article a title and a body before publishing.');
        return;
      }
    }
    setBusyId(p.id);
    try {
      const next = posts.map(x => x.id === p.id
        ? { ...x, published: goingLive, publishedAt: goingLive && !x.publishedAt ? todayISO() : x.publishedAt }
        : x);
      const cleaned = await persist(next, goingLive ? `published ${p.slug || p.title}` : `unpublished ${p.slug || p.title}`);
      const saved = cleaned.find(x => x.id === p.id);
      showAlert(goingLive
        ? `Live now. It's on the public blog at /blog/${saved?.slug} and in the client dashboard under Articles.`
        : 'Unpublished. Removed from the public blog and the dashboard.');
    } catch (e: any) {
      console.error(e);
      showAlert('Could not update that article. Check the console.');
    } finally { setBusyId(null); }
  };

  const removePost = async (p: BlogPost) => {
    const ok = await showConfirm(
      p.published
        ? `Delete "${p.title || 'this article'}"? It is currently live, so it will disappear from the blog and the dashboard.`
        : `Delete "${p.title || 'this draft'}"?`,
    );
    if (!ok) return;
    setBusyId(p.id);
    try {
      await persist(posts.filter(x => x.id !== p.id), 'delete');
    } catch { showAlert('Could not delete that article.'); }
    finally { setBusyId(null); }
  };

  /** Upload a cover image to Storage and put its download URL on the post. */
  const uploadImage = async (p: BlogPost, file: File) => {
    if (!file.type.startsWith('image/')) { showAlert('Choose an image file (JPG, PNG or WebP).'); return; }
    if (file.size > 5 * 1024 * 1024) { showAlert('That image is over 5MB. Please use a smaller one.'); return; }

    setBusyId(p.id);
    setUploadPct(0);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `blogImages/${p.id}/${Date.now()}_${safe}`;
      const task = uploadBytesResumable(storageRef(storage, path), file, { contentType: file.type });
      const url: string = await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref)));
      });
      patch(p.id, x => { x.image = url; });
      showAlert('Image uploaded. Remember to Save (or Publish) to keep it.');
    } catch (e: any) {
      console.error('Image upload failed:', e);
      showAlert(e?.code === 'storage/unauthorized'
        ? 'Upload denied by Storage rules. Sign in as a Firebase-authenticated admin.'
        : 'Image upload failed. Check the console.');
    } finally { setBusyId(null); setUploadPct(null); }
  };

  /** Pull the publisher's own preview image off an article page. */
  const grabImage = async (p: BlogPost, url: string) => {
    const target = (url || '').trim();
    if (!target) { showAlert('Paste the article URL first.'); return; }

    setGrabbingId(p.id);
    try {
      const resp = await fetch(`${API_BASE_URL}/news/og-image?url=${encodeURIComponent(target)}`);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.error || `lookup failed (${resp.status})`);
      patch(p.id, x => { x.image = json.image; });
      showAlert('Cover image taken from the source. Remember to Save (or Publish) to keep it.');
    } catch (e: any) {
      showAlert(e?.message || 'Could not read an image from that page.');
    } finally { setGrabbingId(null); }
  };

  /* ── Feed → drafts ─────────────────────────────────────────────────────── */

  const fetchHeadlines = async () => {
    setGenerating(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/news/feed?view=${genView}`);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.error || 'feed unavailable');
      setHeadlines(json.items || []);
      if (!json.items?.length) showAlert('That feed returned nothing just now. Try again shortly.');
    } catch (e: any) {
      showAlert(`Could not reach the news feed: ${e?.message || 'try again shortly'}.`);
    } finally { setGenerating(false); }
  };

  const brandFor = () => (genView === 'accounting' ? 'corporate' : 'consulting') as BlogPost['brand'];

  const addPost = (p: BlogPost) => {
    setPosts(prev => [p, ...prev]);
    setOpenId(p.id);
    setDirty(true);
    setSavedAt(null);
  };

  /** Quick draft: headline + the publisher's lede + a link back. No model call. */
  const addStub = (it: any) => {
    addPost({
      ...blank(),
      id: `p${Date.now()}`,
      title: String(it.title),
      excerpt: it.summary || '',
      body: it.summary ? `${it.summary}\n\n` : '',
      category: it.tag || 'Insight',
      image: it.image || '',
      readTime: '3 min read',
      publishedAt: String(it.publishedAt || '').slice(0, 10) || todayISO(),
      brand: brandFor(),
      sourceUrl: it.url,
      sourceName: it.source,
    });
    showAlert('Draft added at the top. Edit it, then Publish.');
  };

  /* Failures the admin can fix by simply asking again — Gemini's serving pools
     saturate under load and answer 503, which the backend retries but can't
     always ride out inside one request. Worth one click rather than a dead end. */
  const isTransient = (msg: string) => /overload|try again|rate limit|timed? ?out|temporar/i.test(msg);

  /** Write ONE full article from a headline, one at a time. */
  const writeArticle = async (it: any) => {
    setWritingUrl(it.url || it.id);
    try {
      const resp = await fetch(`${API_BASE_URL}/news/article`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: it.title, summary: it.summary, source: it.source,
          url: it.url, tag: it.tag, view: genView,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.error || `request failed (${resp.status})`);

      const a = json.article;
      addPost({
        ...blank(),
        id: `p${Date.now()}`,
        title: a.title,
        excerpt: a.excerpt || '',
        body: a.body || '',
        category: a.category || it.tag || 'Insight',
        image: it.image || '',
        readTime: a.readTime || readTimeFor(a.body || ''),
        publishedAt: todayISO(),
        brand: brandFor(),
        sourceUrl: a.sourceUrl || it.url,
        sourceName: a.sourceName || it.source,
      });
      showAlert('Article drafted at the top. Read it through, add a cover image, then Publish.');
    } catch (e: any) {
      const msg = e?.message || 'try again';
      if (isTransient(msg)) {
        const again = await showConfirm(`Could not write that article: ${msg}.`, {
          title: 'Gemini is busy', variant: 'warning', confirmText: 'Try again', cancelText: 'Leave it',
        });
        if (again) { setWritingUrl(null); return writeArticle(it); }
      } else {
        showAlert(`Could not write that article: ${msg}.`);
      }
    } finally { setWritingUrl(null); }
  };

  /* ── Derived ───────────────────────────────────────────────────────────── */

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter(p => {
      if (filter === 'live' && !p.published) return false;
      if (filter === 'draft' && p.published) return false;
      if (!q) return true;
      return `${p.title} ${p.excerpt} ${p.category}`.toLowerCase().includes(q);
    });
  }, [posts, filter, query]);

  const liveCount = posts.filter(p => p.published).length;

  const LAYOUT = {
    section: 'Content',
    title: 'Articles',
    icon: PenLine,
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading articles…" />;

  return (
    <AdminLayout
      {...LAYOUT}
      spaced
      subtitle={<>
        {posts.length} total · <b className="text-green-600">{liveCount} live</b> · {posts.length - liveCount} draft{posts.length - liveCount !== 1 ? 's' : ''}
        {' '}· Published articles appear on the public blog <i>and</i> in the client dashboard.
      </>}
      actions={<>
        {dirty && <span className="hint" style={{ fontWeight: 700, color: 'var(--brand-d)' }}>Unsaved changes</span>}
        {savedAt && !dirty && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
        <button onClick={handleSave} disabled={saving} className="btn btn-pri">
          <Save />{saving ? 'Saving…' : 'Save all'}
        </button>
      </>}
    >
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => addPost(blank())} className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50">
          <Plus className="w-4 h-4" /> New article
        </button>
        <div className="flex items-center gap-2 border border-gray-200 bg-white rounded-xl pl-3 pr-1.5 py-1">
          <select value={genView} onChange={e => setGenView(e.target.value as 'consulting' | 'accounting')}
            className="text-sm font-semibold text-gray-700 bg-transparent focus:outline-none py-1.5">
            <option value="consulting">Funding &amp; VC feed</option>
            <option value="accounting">UAE tax &amp; compliance feed</option>
          </select>
          <button onClick={fetchHeadlines} disabled={generating}
            className="flex items-center gap-2 bg-[var(--brand)] text-[var(--brand-on)] font-bold text-sm px-3.5 py-2 rounded-lg hover:bg-[var(--brand-h)] disabled:opacity-60">
            <Sparkles className="w-4 h-4" /> {generating ? 'Fetching…' : 'Fetch headlines'}
          </button>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search articles…"
            className="w-48 border border-gray-200 rounded-xl pl-8 pr-3 py-2 text-sm bg-white focus:outline-none focus:border-[var(--brand)]" />
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden">
          {(['all', 'live', 'draft'] as Filter[]).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs font-bold px-3 py-2 capitalize ${filter === f ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              {f === 'all' ? 'All' : f === 'live' ? 'Live' : 'Drafts'}
            </button>
          ))}
        </div>
        <a href="/blog" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-700">
          <ExternalLink className="w-3.5 h-3.5" /> Public blog
        </a>
      </div>

      {/* ── Headline picker ── */}
      {headlines.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">
              {headlines.length} live headline{headlines.length !== 1 ? 's' : ''} · “Write full article” drafts our own take (about a minute)
            </span>
            <button onClick={() => setHeadlines([])} className="text-[11px] font-bold text-gray-400 hover:text-gray-700">Clear</button>
          </div>
          {headlines.map((it: any) => {
            const busy = writingUrl === (it.url || it.id);
            return (
              <div key={it.id || it.url} className="px-4 py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 leading-snug">{it.title}</p>
                  <p className="text-[11px] text-gray-400 font-semibold mt-1">
                    {it.source}{it.tag ? ` · ${it.tag}` : ''}
                    {it.url && <> · <a href={it.url} target="_blank" rel="noreferrer" className="hover:text-gray-700 underline">source ↗</a></>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => addStub(it)} disabled={!!writingUrl}
                    className="text-xs font-bold text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    Quick draft
                  </button>
                  <button onClick={() => writeArticle(it)} disabled={!!writingUrl}
                    className="flex items-center gap-1.5 text-xs font-bold text-[var(--brand-on)] bg-[var(--brand)] px-3 py-1.5 rounded-lg hover:bg-[var(--brand-h)] disabled:opacity-60">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {busy ? 'Writing…' : 'Write full article'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Article list ── */}
      {shown.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <PenLine className="w-7 h-7 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-semibold">
            {posts.length === 0 ? 'No articles yet. Start with “New article” or fetch a headline.' : 'Nothing matches that filter.'}
          </p>
        </div>
      )}

      {shown.map(p => {
        const open = openId === p.id;
        const busy = busyId === p.id;
        const grabbing = grabbingId === p.id;
        const slug = p.slug || slugify(p.title) || '…';
        return (
          <div key={p.id} className={`bg-white border rounded-2xl shadow-sm overflow-hidden ${p.published ? 'border-green-200' : 'border-gray-200'}`}>
            {/* Row */}
            <div className="flex items-center gap-3 p-3">
              <button onClick={() => setOpenId(open ? null : p.id)} className="text-gray-400 hover:text-gray-700 shrink-0">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>

              <div className="w-16 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0 grid place-items-center">
                {p.image
                  ? <img src={p.image} alt="" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-4 h-4 text-gray-300" />}
              </div>

              <button onClick={() => setOpenId(open ? null : p.id)} className="flex-1 min-w-0 text-left">
                <p className="text-sm font-bold text-gray-900 truncate">{p.title || <span className="text-gray-400">Untitled article</span>}</p>
                <p className="text-[11px] text-gray-400 font-semibold truncate">
                  {p.category} · {p.publishedAt?.slice(0, 10)} · {p.brand === 'both' ? 'both sites' : p.brand === 'corporate' ? 'corporate only' : 'consulting only'}
                </p>
              </button>

              <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${p.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {p.published ? 'Live' : 'Draft'}
              </span>

              {p.published && (
                <div className="hidden md:flex items-center gap-1 shrink-0">
                  <a href={`/blog/${slug}`} target="_blank" rel="noreferrer" title="View on the public blog"
                    className="text-[11px] font-bold text-gray-500 hover:text-gray-800 px-2 py-1 rounded-lg hover:bg-gray-50">/blog ↗</a>
                  <a href={`/articles/${slug}`} target="_blank" rel="noreferrer" title="View in the client dashboard"
                    className="text-[11px] font-bold text-gray-500 hover:text-gray-800 px-2 py-1 rounded-lg hover:bg-gray-50">/articles ↗</a>
                </div>
              )}

              <button onClick={() => togglePublish(p)} disabled={busy}
                className={`flex items-center gap-1.5 text-xs font-extrabold px-3 py-2 rounded-lg shrink-0 disabled:opacity-60 ${
                  p.published ? 'border border-gray-200 text-gray-600 hover:bg-gray-50' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : p.published ? <EyeOff className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                {busy ? '…' : p.published ? 'Unpublish' : 'Publish'}
              </button>

              <button onClick={() => removePost(p)} disabled={busy} className="text-gray-300 hover:text-red-500 shrink-0 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Editor */}
            {open && (
              <div className="border-t border-gray-100 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50/50">
                <div className="sm:col-span-2"><label className={lbl}>Title</label>
                  <input className={inputCls} value={p.title} onChange={e => patch(p.id, x => { x.title = e.target.value; })} placeholder="Article headline" /></div>

                {/* Cover image */}
                <div className="sm:col-span-2">
                  <label className={lbl}>Cover image</label>
                  <div className="flex items-start gap-3">
                    <div className="w-32 h-20 rounded-lg bg-white border border-gray-200 overflow-hidden grid place-items-center shrink-0">
                      {p.image
                        ? <img src={p.image} alt="" className="w-full h-full object-cover" />
                        : <ImageIcon className="w-5 h-5 text-gray-300" />}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <input className={inputCls} value={p.image} onChange={e => patch(p.id, x => { x.image = e.target.value; })}
                        placeholder="Paste an image URL, or upload →" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <input ref={el => { fileInputs.current[p.id] = el; }} type="file" accept="image/*" className="hidden"
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(p, f); e.target.value = ''; }} />
                        <button onClick={() => fileInputs.current[p.id]?.click()} disabled={busy}
                          className="flex items-center gap-1.5 text-xs font-bold text-gray-700 border border-gray-200 bg-white px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-60">
                          {busy && uploadPct !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {busy && uploadPct !== null ? `Uploading ${uploadPct}%` : 'Upload image'}
                        </button>

                        {/* Take the publisher's own preview image off the article page. */}
                        <button onClick={() => grabImage(p, grabUrl[p.id] ?? p.sourceUrl ?? '')}
                          disabled={grabbing || (!p.sourceUrl && !(grabUrl[p.id] || '').trim())}
                          title={p.sourceUrl && !grabUrl[p.id] ? `Read the cover image from ${p.sourceName || 'the source article'}` : 'Read the cover image from the article URL'}
                          className="flex items-center gap-1.5 text-xs font-bold text-gray-700 border border-gray-200 bg-white px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                          {grabbing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageDown className="w-3.5 h-3.5" />}
                          {grabbing ? 'Reading…' : 'From source'}
                        </button>

                        {p.image && (
                          <button onClick={() => patch(p.id, x => { x.image = ''; })}
                            className="text-xs font-bold text-gray-400 hover:text-red-500">Remove</button>
                        )}
                        <span className="text-[10px] text-gray-400 font-medium">JPG, PNG or WebP · up to 5MB</span>
                      </div>

                      {/* The article to read the picture from. Pre-filled with the
                          post's own source; editable because half our feed items
                          are Google News links, which redirect rather than carry
                          the publisher's page. */}
                      <div className="flex items-center gap-2">
                        <input className={`${inputCls} text-[11px] py-1.5`}
                          value={grabUrl[p.id] ?? p.sourceUrl ?? ''}
                          onChange={e => setGrabUrl(m => ({ ...m, [p.id]: e.target.value }))}
                          placeholder="https://publisher.com/the-article — then press “From source”" />
                        {(grabUrl[p.id] ?? p.sourceUrl) && (
                          <a href={grabUrl[p.id] ?? p.sourceUrl} target="_blank" rel="noreferrer"
                            title="Open the article" className="text-gray-400 hover:text-gray-700 p-1.5 shrink-0">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sm:col-span-2"><label className={lbl}>URL slug <span className="normal-case font-medium text-gray-300">(blank generates it from the title)</span></label>
                  <div className="flex items-center gap-2">
                    <input className={inputCls} value={p.slug} onChange={e => patch(p.id, x => { x.slug = e.target.value; })} placeholder={slugify(p.title) || 'my-article'} />
                    <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/blog/${slug}`); showAlert('Public link copied.'); }}
                      title="Copy the public link" className="text-gray-400 hover:text-gray-700 p-2 shrink-0"><Copy className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">/blog/{slug} &nbsp;·&nbsp; /articles/{slug}</p></div>

                <div className="sm:col-span-2"><label className={lbl}>Excerpt <span className="normal-case font-medium text-gray-300">(shown on cards)</span></label>
                  <textarea className={areaCls} rows={2} value={p.excerpt} onChange={e => patch(p.id, x => { x.excerpt = e.target.value; })}
                    placeholder="One or two sentences summarising the article." /></div>

                <div className="sm:col-span-2">
                  <div className="flex items-baseline justify-between">
                    <label className={lbl}>Body <span className="normal-case font-medium text-gray-300">(blank line between paragraphs)</span></label>
                    <span className="text-[10px] text-gray-400 font-semibold">
                      {p.body.trim() ? `${p.body.trim().split(/\s+/).length} words` : ''}
                    </span>
                  </div>
                  <textarea className={areaCls} rows={12} value={p.body} onChange={e => patch(p.id, x => { x.body = e.target.value; })}
                    placeholder={'Write the article here.\n\nLeave a blank line to start a new paragraph.'} /></div>

                <div><label className={lbl}>Category</label>
                  <input className={inputCls} value={p.category} onChange={e => patch(p.id, x => { x.category = e.target.value; })} placeholder="e.g. Tax & Compliance" /></div>
                <div><label className={lbl}>Read time</label>
                  <div className="flex items-center gap-2">
                    <input className={inputCls} value={p.readTime} onChange={e => patch(p.id, x => { x.readTime = e.target.value; })} placeholder="5 min read" />
                    <button onClick={() => patch(p.id, x => { x.readTime = readTimeFor(x.body); })}
                      className="text-[11px] font-bold text-gray-500 hover:text-gray-800 border border-gray-200 bg-white px-2 py-2 rounded-lg shrink-0">Auto</button>
                  </div></div>

                <div><label className={lbl}>Author</label>
                  <input className={inputCls} value={p.author} onChange={e => patch(p.id, x => { x.author = e.target.value; })} /></div>
                <div><label className={lbl}>Publish date</label>
                  <input type="date" className={inputCls} value={(p.publishedAt || '').slice(0, 10)} onChange={e => patch(p.id, x => { x.publishedAt = e.target.value; })} /></div>

                <div className="sm:col-span-2"><label className={lbl}>Show on</label>
                  <select className={inputCls} value={p.brand} onChange={e => patch(p.id, x => { x.brand = e.target.value as BlogPost['brand']; })}>
                    <option value="both">Both sites</option>
                    <option value="corporate">connectcorporate.com only (Accounting &amp; Corporate)</option>
                    <option value="consulting">conncct.com only (Consulting)</option>
                  </select></div>

                {p.sourceUrl && (
                  <div className="sm:col-span-2 text-[11px] text-gray-500 font-medium bg-white border border-gray-200 rounded-lg px-3 py-2">
                    Drafted from a headline by <b>{p.sourceName || 'the publisher'}</b>: {' '}
                    <a href={p.sourceUrl} target="_blank" rel="noreferrer" className="underline hover:text-gray-800">source ↗</a>.
                    The article page credits and links them.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="h-8" />
    </AdminLayout>
  );
}
